/**
 * Global financial engine — live Supabase tariff + profit bracket lookup.
 */
import { supabase } from '../lib/supabaseClient';
export * from './globalFinancialEngineCore.js';
import {
  calculateGlobalLandedCost,
  inferCategorySlug,
  mapToGlobalCategorySlug,
  normalizeCategorySlug,
  resolveGlobalTariff,
  resolveProfitBracket,
} from './globalFinancialEngineCore.js';

const tariffCache = new Map();
const bracketCache = new Map();

function tariffKey(country, category) {
  return `${String(country).toUpperCase()}:${normalizeCategorySlug(category)}`;
}

export async function fetchRegionalTariff(destinationCountry = 'BB', categorySlug = 'baby_essentials') {
  const country = String(destinationCountry || 'BB').toUpperCase();
  const category = mapToGlobalCategorySlug(categorySlug);
  const key = tariffKey(country, category);
  if (tariffCache.has(key)) return tariffCache.get(key);

  let row = null;
  if (supabase) {
    const { data, error } = await supabase
      .from('regional_customs_tariffs')
      .select(
        'country_code, country_name, category_slug, import_duty_pct, local_vat_gct_pct, environmental_levy_pct, customs_processing_flat, local_currency_code, currency_exchange_rate',
      )
      .eq('country_code', country)
      .eq('category_slug', category)
      .maybeSingle();
    if (!error && data) row = data;
  }

  const resolved = resolveGlobalTariff(country, category, row);
  tariffCache.set(key, resolved);
  return resolved;
}

export async function fetchProfitBracket(itemCost) {
  const cost = Number(itemCost) || 0;
  const cacheKey = cost.toFixed(2);
  if (bracketCache.has(cacheKey)) return bracketCache.get(cacheKey);

  let row = null;
  if (supabase) {
    const { data, error } = await supabase.rpc('lookup_profit_bracket', { p_item_cost: cost });
    if (!error && data) row = data;
  }

  const resolved = resolveProfitBracket(cost, row);
  bracketCache.set(cacheKey, resolved);
  return resolved;
}

export async function calculateGlobalLandedCostAsync(input) {
  const category = mapToGlobalCategorySlug(
    input.categorySlug || inferCategorySlug(input.title || '', input.categorySlug || ''),
  );
  const [tariff, bracket] = await Promise.all([
    fetchRegionalTariff(input.destinationCountry || 'BB', category),
    fetchProfitBracket(input.rawMarketplacePrice ?? input.itemCost),
  ]);
  return calculateGlobalLandedCost({
    ...input,
    categorySlug: category,
    tariff,
    bracket,
  });
}

/** @deprecated alias */
export const calculateLandedFailsafePriceAsync = calculateGlobalLandedCostAsync;
